import { Component } from '@angular/core';
import { FormBuilder, FormControl, Validators, FormGroup } from '@angular/forms';
import { MbscSelectChangeEvent } from '@mobiscroll/angular';
import { SelectBase } from '@mobiscroll/angular/dist/js/core/components/select/select';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastNotificationService } from '../../../core/toast-notification.service';
import { UIBlockingService } from '../../../core/ui-blocking.service';
import { MinorityCertificateType } from '../../../sign-up/DTOs/minority-certificate-type';
import { SignUpService } from '../../../sign-up/sign-up.service';
import { MemberUserManagementService } from '../../member-user-management/member-user-management.service';
import { DumbCompanyDetails } from '../DTOs/dumb-company';
import { MembershipManagementService } from '../membership-management.service';
import { NumberOfEmployeesOption } from '../../../sign-up/DTOs/number-of-employees-option';

@Component({
    selector: 'app-edit-company-info-modal',
    templateUrl: './edit-company-info-modal.component.html',
    providers: [UIBlockingService],
})
export class EditCompanyInfoModalComponent {
    company: DumbCompanyDetails;
    isCompanyLiaison: boolean = false;

    constructor(private activeModal: NgbActiveModal,
        private fb: FormBuilder,
        private membershipService: MembershipManagementService,
        private memberAdminService: MemberUserManagementService,
        private notificationService: ToastNotificationService,
        private blockUI: UIBlockingService,
        private signUpService: SignUpService) { }

    companyNameCtrl = new FormControl("companyName", [Validators.required]);
    phoneNumberCtrl = new FormControl("phoneNumber", [Validators.required]);
    numberOfEmployeesCtrl = new FormControl("numberOfEmployees", [Validators.required, Validators.min(0), Validators.pattern("^[0-9]*$")]);
    streetCtrl = new FormControl("street", [Validators.required]);
    cityCtrl = new FormControl("city", [Validators.required]);
    zipCtrl = new FormControl("zip", [Validators.required]);
    stateCtrl = new FormControl("state", [Validators.required]);
    isPrivateAddressCtrl = new FormControl("isPrivateAddress");

    minorityOwnedCertificateCtrl = new FormControl("minorityOwnedCertificates");
    isAlaskanNativeCtrl = new FormControl("isAlaskanNative");
    isAsianCtrl = new FormControl("isAsian");
    isBlackCtrl = new FormControl("isBlack");
    isDisabilityCtrl = new FormControl("isDisability");
    isHispanicCtrl = new FormControl("isHispanic");
    isLGBTQCtrl = new FormControl("isLGBTQ");
    isVeteranCtrl = new FormControl("isVeteran");
    isWomenCtrl = new FormControl("isWomen");
    isOtherMinorityCtrl = new FormControl("isOtherMinority");
    noneOfTheAboveCtrl = new FormControl("noneOfTheAbove");
    industryCtrl = new FormControl("industry", [Validators.required]);

    minorityCertificateTypes: MinorityCertificateType[] = [];
    numberOfEmployeesOptions: NumberOfEmployeesOption[] = [];
    selectedCerts: number[];
    industryOptions: string[];

    manageCompanyInfoForm: FormGroup = new FormGroup({
        companyName: this.companyNameCtrl,
        phoneNumber: this.phoneNumberCtrl,
        numberOfEmployees: this.numberOfEmployeesCtrl,
        street: this.streetCtrl,
        city: this.cityCtrl,
        zip: this.zipCtrl,
        industry: this.industryCtrl,
        state: this.stateCtrl,
        isAlaskanNative: this.isAlaskanNativeCtrl,
        isAsian: this.isAsianCtrl,
        isBlack: this.isBlackCtrl,
        isDisability: this.isDisabilityCtrl,
        isHispanic: this.isHispanicCtrl,
        isLGBTQ: this.isLGBTQCtrl,
        isVeteran: this.isVeteranCtrl,
        isWomen: this.isWomenCtrl,
        isOtherMinority: this.isOtherMinorityCtrl,
        noneOfTheAbove: this.noneOfTheAboveCtrl,
        isPrivateAddress: this.isPrivateAddressCtrl,
    });
    companyName() { return this.manageCompanyInfoForm.get('companyName').value }
    phoneNumber() { return this.manageCompanyInfoForm.get('phoneNumber').value }
    numberOfEmployees() { return this.manageCompanyInfoForm.get('numberOfEmployees').value }
    street() { return this.manageCompanyInfoForm.get('street').value }
    city() { return this.manageCompanyInfoForm.get('city').value }
    zip() { return this.manageCompanyInfoForm.get('zip').value }
    state() { return this.manageCompanyInfoForm.get('state').value }
    isAlaskanNative() { return this.manageCompanyInfoForm.get('isAlaskanNative').value }
    isAsian() { return this.manageCompanyInfoForm.get('isAsian').value }
    isBlack() { return this.manageCompanyInfoForm.get('isBlack').value }
    isDisability() { return this.manageCompanyInfoForm.get('isDisability').value }
    isHispanic() { return this.manageCompanyInfoForm.get('isHispanic').value }
    isLGBTQ() { return this.manageCompanyInfoForm.get('isLGBTQ').value }
    isVeteran() { return this.manageCompanyInfoForm.get('isVeteran').value }
    isWomen() { return this.manageCompanyInfoForm.get('isWomen').value }
    isOtherMinority() { return this.manageCompanyInfoForm.get('isOtherMinority').value }
    isPrivateAddress() { return this.manageCompanyInfoForm.get('isPrivateAddress').value }
    industry() { return this.manageCompanyInfoForm.get('industry').value }

    ngOnInit() {
        this.isCompanyLiaison = this.company.liaison.contactId == localStorage.getItem("contactId");
        this.signUpService
            .getminorityCertificateTypes()
            .subscribe(
                {
                    next: (data) => {
                        this.minorityCertificateTypes = data.minorityCertificateTypes;
                        this.numberOfEmployeesOptions = data.numberOfEmployeesOptions;
                        this.industryOptions = data.industryOptions;
                    },
                    error: (err) => {
                        this.notificationService.error(err);
                    },
                    complete: () => {
                        this.resetManageCompanyInfoForm();
                    }
                });
    }

    private resetManageCompanyInfoForm(): void {
        this.manageCompanyInfoForm.reset();
        this.manageCompanyInfoForm.patchValue({
            companyName: this.company.companyName,
            phoneNumber: this.company.businessPhone,
            numberOfEmployees: this.company.numberOfEmployees,
            street: this.company.primaryStreet,
            city: this.company.primaryCity,
            zip: this.company.primaryZip,
            state: this.company.primaryState,
            isAlaskanNative: this.company.isAlaskanOwned,
            industry: this.company.industry,
            isAsian: this.company.isAsianOwned,
            isBlack: this.company.isBlackOwned,
            isDisability: this.company.isDisabilityOwned,
            isHispanic: this.company.isHispanicOwned,
            isLGBTQ: this.company.isLGBTQOwned,
            isVeteran: this.company.isVeteranOwned,
            isWomen: this.company.isWomenOwned,
            isOtherMinority: this.company.isOtherMinorityOwned,
            noneOfTheAbove: this.company.noneOfTheAbove,
            isPrivateAddress: this.company.privateAddress
        });
        this.selectedCerts = this.company.certificateTypes;
    }

    changedMSelect(args: MbscSelectChangeEvent) {
        //console.log(args);
    }

    save() {
        for (var i in this.manageCompanyInfoForm.controls) {
            this.manageCompanyInfoForm.controls[i].markAsTouched();
        }
        if (!this.manageCompanyInfoForm.valid) {
            return false;
        }
        return this.saveCompanyDetails();
    }

    saveCompanyDetails() {
        this.company.companyName = this.companyName();
        this.company.businessPhone = this.phoneNumber();
        this.company.numberOfEmployees = this.numberOfEmployees();
        this.company.primaryStreet = this.street();
        this.company.primaryCity = this.city();
        this.company.primaryZip = this.zip();
        this.company.primaryState = this.state();
        this.company.certificateTypes = this.selectedCerts;
        this.company.isAlaskanOwned = this.isAlaskanNative();
        this.company.isAsianOwned = this.isAsian();
        this.company.isBlackOwned = this.isBlack();
        this.company.isDisabilityOwned = this.isDisability();
        this.company.isHispanicOwned = this.isHispanic();
        this.company.industry = this.industry();
        this.company.isLGBTQOwned = this.isLGBTQ();
        this.company.isVeteranOwned = this.isVeteran();
        this.company.isWomenOwned = this.isWomen();
        this.company.isOtherMinorityOwned = this.isOtherMinority();
        this.company.privateAddress = this.isPrivateAddress();

        this.blockUI.start();
        this.membershipService.UpdateCompanyDetails(this.company)
            .subscribe({
                next: (data) => {
                    this.blockUI.stop();
                    this.activeModal.close(data);
                },
                error: (err) => {
                    console.log(err)
                    this.notificationService.error(err.error.message);
                    this.blockUI.stop();
                },
            });
        return true;
    }
    dismiss(): void {
        this.activeModal.dismiss();
    }
}
